<script setup lang="ts">
import { twMerge } from "tailwind-merge";

import type { PxlIconName } from "~/common/components/U/Icon";
import UThicknessRange from "~/common/components/U/Range/ThicknessRange.vue";
import { ShapeStrokeTypeOptions } from "~/telestrations/constants/colors";
import { getLineTypeFromDash, LINE_TYPE_DASH_MAP, LineType } from "~/telestrations/constants/line-types";
import { TELESTRATION_TOOLTIP_CONTAINER } from "~/telestrations/constants/tooltip";

const { t } = useI18n();
const shapesStore = useShapesStore();
const isMenuOpened = ref(false);

const options = computed(() => {
  if (shapesStore.selectedShape) {
    return ShapeStrokeTypeOptions[shapesStore.selectedShape.type].map((option) => ({
      ...option,
      label: t(option.labelKey),
    }));
  }

  return [];
});
const selectedStrokeType = computed(() => {
  if (shapesStore.selectedShape) {
    return getLineTypeFromDash(shapesStore.selectedShape.dash);
  }

  return LineType.SOLID;
});

const inputClasses = computed(() =>
  twMerge(
    "!border-none !gap-x-2 px-1.5",
    isMenuOpened.value ? "!bg-neutral-light-100 dark:!bg-neutral-dark-500" : "hover:!bg-neutral-light-100 dark:hover:!bg-neutral-dark-500"
  )
);

function onShow() {
  isMenuOpened.value = true;
}

function onHide() {
  isMenuOpened.value = false;
}

function onShapeStrokeChange(value: LineType) {
  if (!shapesStore.selectedShape) {
    return;
  }

  shapesStore.updateShape(shapesStore.selectedShape, { dash: LINE_TYPE_DASH_MAP.get(value) });
}

// Thickness control (strokeWidth)
const thicknessValue = ref<number>(1);

watchEffect(() => {
  thicknessValue.value = shapesStore.selectedShape?.strokeWidth ?? 1;
});

function onThicknessInput(e: Event) {
  const target = e.target as HTMLInputElement | null;
  if (!target) return;

  const inputValue = target.value.trim();
  let val = parseFloat(inputValue);

  if (!Number.isFinite(val)) {
    val = 1;
  }

  // Clamp thickness between 1 and 20 px
  val = Math.max(1, Math.min(20, val));
  thicknessValue.value = val;

  if (shapesStore.selectedShape) {
    shapesStore.updateShape(shapesStore.selectedShape, { strokeWidth: val });
    // Update transformer padding/position to account for stroke width change
    shapesStore.attachTransformerToShape(shapesStore.selectedShape);
    shapesStore.updateTransformerPosition();
  }
}
</script>

<template>
  <USelectMenu
    :model-value="selectedStrokeType"
    :options="options"
    :input-class="inputClasses"
    list-class="grid grid-cols-3 p-2 gap-2"
    item-class="rounded aria-selected:text-blue-500 text-black aria-selected:bg-neutral-light-100 dark:aria-selected:bg-neutral-dark-500 hover:bg-neutral-light-100 dark:aria-selected:text-blue-500"
    variant="inline"
    :divider="false"
    :hide-trailing-icon="true"
    :render-in-portal="false"
    :has-empty-state="false"
    @show="onShow"
    @hide="onHide"
    @update:model-value="onShapeStrokeChange"
  >
    <template #header>
      <div class="font-red-hat-display p-2 dark:bg-neutral-dark-700" @click.stop>
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-medium text-secondary dark:text-neutral-dark-200">
            {{ t("labels.thickness") }}
          </span>
        </div>
        <UThicknessRange
          :model-value="thicknessValue"
          :min="1"
          :max="20"
          :step="1"
          class="mt-2 h-3"
          @update:model-value="onThicknessInput"
        />
      </div>
    </template>
    <template #option="{ option }">
      <UIcon class="size-6 rounded-full" :name="`border-type-${option.value}` as PxlIconName" />
    </template>
    <UIcon
      v-tooltip="{
        content: t('labels.change_border_type'),
        container: TELESTRATION_TOOLTIP_CONTAINER,
      }"
      class="size-6"
      name="line-type"
    />
  </USelectMenu>
</template>
